import { Span, Text } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { useValidateOtp, ValidateOtpError } from "@src/appV2/Auth/api/useValidateOtp";
import FetchOtpButtonInDevelopmentMode from "@src/appV2/Debug/components/FetchOtpButtonInDevelopmentMode";
import { extractPhoneNumberDigits, useToast } from "@src/appV2/lib";
import { OtpFieldBoxes } from "@src/appV2/lib/FormFields/OtpField/OtpFieldBoxes";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isAxiosError } from "axios";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useRequestProfileOtp } from "../../api/useRequestProfileOtp";
import { otpFormSchema } from "../../types/otpFormSchema";

interface VerifyPhoneOtpStepProps {
  onVerified: () => void;
}

export function VerifyPhoneOtpStep({ onVerified }: VerifyPhoneOtpStepProps) {
  const worker = useDefinedWorker();
  const { showErrorToast, showSuccessToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [otpSent, setOtpSent] = useState(false);

  const { mutateAsync: requestOtp, isLoading: isRequestingOtp } = useRequestProfileOtp();
  const { mutateAsync: validateOtp, isLoading: isVerifyingOtp } = useValidateOtp();

  const formMethods = useForm({
    defaultValues: {
      otp: "",
    },
    resolver: zodResolver(otpFormSchema),
    mode: "onChange",
    reValidateMode: "onSubmit",
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    formState: { isSubmitting, isValid },
  } = formMethods;

  const { countdownIsLoading, startTimer, secondsLeft } = useCountdownTimer({
    durationInSeconds: 25,
  });

  const phoneNumber = worker.phone;

  // Mask phone number to show only last 4 digits
  const getMaskedPhone = (phone: string): string => {
    if (!phone) {
      return "";
    }

    const digits = extractPhoneNumberDigits(phone);

    if (digits.length < 4) {
      return phone;
    }

    const lastFour = digits.slice(-4);

    return `***-***-${lastFour}`;
  };

  const maskedPhone = phoneNumber ? getMaskedPhone(phoneNumber) : "";

  const handleSendOtp = async () => {
    if (!phoneNumber || countdownIsLoading) {
      return;
    }

    try {
      const phoneNumberDigits = extractPhoneNumberDigits(phoneNumber);
      await requestOtp({
        phone: phoneNumberDigits,
      });
      setOtpSent(true);
      startTimer();
      showSuccessToast("Verification code sent");
    } catch (error) {
      if (isAxiosError(error)) {
        const message =
          (error.response?.data as { message?: string })?.message ??
          "Failed to send verification code";
        showErrorToast(message);
      } else {
        showErrorToast("Failed to send verification code. Please try again.");
      }
    }
  };

  const handleOtpValidationError = (errorMessage: string, errorCode?: string) => {
    if (errorCode === ValidateOtpError.INVALID_OTP || errorMessage === "Invalid OTP") {
      setError("otp", {
        message: "Invalid verification code. Please try again.",
      });
    } else if (errorCode === ValidateOtpError.OTP_EXPIRED || errorMessage === "Otp Expired") {
      setError("otp", {
        message: "Verification code has expired. Please request a new one.",
      });
    } else {
      setError("otp", {
        message: errorMessage || "Failed to verify code. Please try again.",
      });
    }
  };

  const onSubmit = async (formData: { otp: string }) => {
    if (!phoneNumber) {
      showErrorToast("Phone number is required");
      return;
    }

    // Clear any previous errors before validating
    clearErrors("otp");

    try {
      const phoneNumberDigits = extractPhoneNumberDigits(phoneNumber);
      const response = await validateOtp({
        phone: phoneNumberDigits,
        otp: formData.otp,
        isSignup: false,
      });

      if (response.data.message === "otpIsValid") {
        onVerified();
        return;
      }

      handleOtpValidationError(response.data.message);
    } catch (error) {
      if (isAxiosError(error) && error.response?.data) {
        const responseData = error.response.data as { message?: string; error?: string };
        handleOtpValidationError(responseData.message ?? "", responseData.error);
      } else {
        showErrorToast("Failed to verify code. Please try again.");
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Stack
        ref={formRef}
        component="form"
        sx={{ paddingY: 4, gap: 12 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={6} marginBottom={12}>
          <Stack spacing={2}>
            <Text variant="body2">
              To change your email, we need to verify your phone number first for security purposes.
            </Text>
            {otpSent ? (
              <Text variant="body2">
                We&apos;ve sent a verification code to{" "}
                <Span sx={{ whiteSpace: "nowrap" }}>
                  <b>{maskedPhone}</b>
                </Span>
                .
              </Text>
            ) : (
              <Text variant="body2">
                We&apos;ll send a verification code to{" "}
                <Span sx={{ whiteSpace: "nowrap" }}>
                  <b>{maskedPhone}</b>
                </Span>
                .
              </Text>
            )}
          </Stack>
          {otpSent && (
            <>
              <OtpFieldBoxes name="otp" />
              <FetchOtpButtonInDevelopmentMode
                phoneNumber={phoneNumber}
                onSuccess={(otp) => {
                  setValue("otp", otp);
                  formRef.current?.requestSubmit();
                }}
              />
            </>
          )}
        </Stack>
        <Stack spacing={4} sx={{ paddingTop: 12 }}>
          {otpSent ? (
            <>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                isLoading={isSubmitting || isVerifyingOtp}
                disabled={!isValid || isRequestingOtp}
              >
                Continue
              </LoadingButton>
              <Button
                fullWidth
                variant="text"
                size="small"
                disabled={countdownIsLoading || isRequestingOtp}
                onClick={handleSendOtp}
              >
                {countdownIsLoading
                  ? `Resend code in ${secondsLeft}s`
                  : "Didn't receive a code? Resend"}
              </Button>
            </>
          ) : (
            <Stack paddingTop={12}>
              <LoadingButton
                fullWidth
                variant="contained"
                isLoading={isRequestingOtp}
                onClick={handleSendOtp}
              >
                Send Code
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </Stack>
    </FormProvider>
  );
}
